import type firebase from 'firebase/app'
import { getCurrentFirebaseUser, getFirebaseFirestore } from './client'
import type { DashboardUserProfile } from './normalizers'

function requireShortVideoFirestore() {
  const firestore = getFirebaseFirestore()

  if (!firestore) {
    throw new Error('Cloud Firestore is not configured for this environment.')
  }

  return firestore
}

function requireCurrentUser() {
  const user = getCurrentFirebaseUser()

  if (!user) {
    throw new Error('Not authorized')
  }

  return user
}

function getAuthorSnapshot(profile: DashboardUserProfile) {
  return {
    avatar: profile.imageUrl ?? null,
    firstname: profile.firstname ?? '',
    lastname: profile.lastname ?? '',
    middlename: profile.middlename ?? '',
  }
}

function toDocumentList<T>(snapshot: firebase.firestore.QuerySnapshot) {
  return snapshot.docs.map((document) => ({
    ...(document.data() as Omit<T, 'id'>),
    id: document.id,
  }))
}

function shuffleList<T>(items: T[]) {
  for (let index = items.length - 1; index > 0; index -= 1) {
    const swapIndex = Math.floor(Math.random() * (index + 1))
    const current = items[index]
    items[index] = items[swapIndex] as T
    items[swapIndex] = current as T
  }

  return items
}

const SHORT_VIDEO_MILLISECONDS_THRESHOLD = 1000

function normalizeShortVideoTimingSeconds(value: unknown) {
  const numericValue =
    typeof value === 'number'
      ? value
      : typeof value === 'string' && value.trim() !== ''
        ? Number(value)
        : Number.NaN

  if (!Number.isFinite(numericValue)) {
    return undefined
  }

  return numericValue >= SHORT_VIDEO_MILLISECONDS_THRESHOLD ? numericValue / 1000 : numericValue
}

function getShortVideoTimingSeconds(
  item: Record<string, unknown>,
  keys: readonly string[],
) {
  for (const key of keys) {
    const value = normalizeShortVideoTimingSeconds(item[key])

    if (typeof value === 'number') {
      return value
    }
  }

  return undefined
}
function normalizeTranslations(
  value: unknown,
): ShortVideoTranslation[] {
  if (!Array.isArray(value)) {
    return []
  }

  return value.flatMap((item) => {
    if (!item || typeof item !== 'object') {
      return []
    }

    const code = typeof item.code === 'string' ? item.code : ''
    const text = typeof item.text === 'string' ? item.text : ''

    if (!code && !text) {
      return []
    }

    return [{ code, text }]
  })
}

function normalizeTranscriptSegments(
  value: unknown,
): ShortVideoTranscriptSegment[] {
  if (!Array.isArray(value)) {
    return []
  }

  return value.flatMap((item) => {
    if (!item || typeof item !== 'object') {
      return []
    }

    const segment = item as Record<string, unknown>

    return [
      {
        end: getShortVideoTimingSeconds(segment, ['end', 'endTime', 'end_time', 'endAt', 'end_at']),
        main: typeof segment.main === 'boolean' ? segment.main : undefined,
        start: getShortVideoTimingSeconds(segment, ['start', 'startTime', 'start_time', 'startAt', 'start_at']),
        text: typeof segment.text === 'string' ? segment.text : undefined,
        translates: normalizeTranslations(segment.translates),
      },
    ]
  })
}

function normalizeShortVideo(
  document: firebase.firestore.QueryDocumentSnapshot | firebase.firestore.DocumentSnapshot,
): ShortVideo {
  const data = (document.data() ?? {}) as Omit<ShortVideo, 'id'>

  return {
    ...data,
    content: normalizeTranscriptSegments(data.content),
    id: document.id,
    translates: normalizeTranslations(data.translates),
  }
}

function toShortVideoList(snapshot: firebase.firestore.QuerySnapshot) {
  return snapshot.docs.map((document) => normalizeShortVideo(document))
}

export interface ShortVideoTranslation {
  readonly code: string
  readonly text: string
}

export interface ShortVideoTranscriptSegment {
  readonly end?: number
  readonly main?: boolean
  readonly start?: number
  readonly text?: string
  readonly translates?: ShortVideoTranslation[]
}

export interface ShortVideo {
  readonly audio?: string
  readonly channelId?: string
  readonly content: ShortVideoTranscriptSegment[]
  readonly featuredImage?: string
  readonly id: string
  readonly status?: string
  readonly title?: string
  readonly topicId?: string
  readonly translates?: ShortVideoTranslation[]
  readonly youtubeId?: string
}

export interface ShortVideoTopic {
  readonly featuredImage?: string
  readonly id: string
  readonly status?: string
  readonly title?: string
}

export interface ShortVideoChannel {
  readonly createdAt?: number
  readonly featuredImage?: string
  readonly id: string
  readonly registered?: number
  readonly status?: string
  readonly title?: string
}

export interface ShortVideoBookmark {
  readonly author: {
    readonly avatar?: string | null
    readonly firstname?: string
    readonly lastname?: string
    readonly middlename?: string
  }
  readonly authorId: string
  readonly createdAt: number
  readonly id: string
  readonly type?: string
  readonly videoId: string
}

export interface ShortVideoSubscribe {
  readonly author: {
    readonly avatar?: string | null
    readonly firstname?: string
    readonly lastname?: string
    readonly middlename?: string
  }
  readonly authorId: string
  readonly channelId: string
  readonly createdAt: number
  readonly id: string
}

export interface ShortVideoLike {
  readonly author: {
    readonly avatar?: string | null
    readonly firstname?: string
    readonly lastname?: string
    readonly middlename?: string
  }
  readonly authorId: string
  readonly createdAt: number
  readonly id: string
  readonly type?: string
  readonly videoId: string
}

export interface ShortVideoPageResult {
  readonly total: number
  readonly videos: ShortVideo[]
}

export async function getPublishedShortVideosPage({
  anchorId,
  direction = 'initial',
  limit = 8,
}: {
  readonly anchorId?: string
  readonly direction?: 'initial' | 'next' | 'prev'
  readonly limit?: number
}): Promise<ShortVideoPageResult> {
  const firestore = requireShortVideoFirestore()
  const baseQuery = firestore.collection('videos').where('status', '==', 'published')

  let pageQuery: firebase.firestore.Query = baseQuery.limit(limit)

  if (anchorId && direction !== 'initial') {
    const anchorSnapshot = await firestore.collection('videos').doc(anchorId).get()

    if (anchorSnapshot.exists) {
      pageQuery =
        direction === 'next'
          ? baseQuery.startAfter(anchorSnapshot).limit(limit)
          : baseQuery.endBefore(anchorSnapshot).limit(limit)
    }
  }

  const [pageSnapshot, totalSnapshot] = await Promise.all([
    pageQuery.get(),
    baseQuery.get(),
  ])

  return {
    total: totalSnapshot.size,
    videos: toShortVideoList(pageSnapshot).reverse(),
  }
}

export async function searchPublishedShortVideos(query: string) {
  const firestore = requireShortVideoFirestore()
  const snapshot = await firestore
    .collection('videos')
    .where('status', '==', 'published')
    .get()

  const normalizedQuery = query.trim().toLowerCase()

  if (!normalizedQuery) {
    return [] as ShortVideo[]
  }

  return toShortVideoList(snapshot)
    .filter((video) => (video.title ?? '').toLowerCase().includes(normalizedQuery))
    .slice(0, 8)
    .reverse()
}

export async function getRelatedShortVideosByTopic({
  excludeVideoId,
  topicId,
}: {
  readonly excludeVideoId: string
  readonly topicId?: string
}) {
  if (!topicId) {
    return [] as ShortVideo[]
  }

  const firestore = requireShortVideoFirestore()
  const snapshot = await firestore
    .collection('videos')
    .where('status', '==', 'published')
    .where('topicId', '==', topicId)
    .limit(4)
    .get()

  return toShortVideoList(snapshot)
    .filter((video) => video.id !== excludeVideoId)
    .slice(0, 3)
    .reverse()
}

export async function getPublishedShortVideoTopics() {
  const firestore = requireShortVideoFirestore()
  const snapshot = await firestore
    .collection('topics')
    .where('status', '==', 'published')
    .get()

  return toDocumentList<ShortVideoTopic>(snapshot).reverse()
}

export async function getPublishedShortVideoChannels() {
  const firestore = requireShortVideoFirestore()
  const snapshot = await firestore
    .collection('channels')
    .where('status', '==', 'published')
    .orderBy('registered', 'desc')
    .orderBy('createdAt', 'desc')
    .get()

  return shuffleList(toDocumentList<ShortVideoChannel>(snapshot).reverse())
}

export async function getCurrentUserVideoBookmarks() {
  const firestore = requireShortVideoFirestore()
  const user = requireCurrentUser()
  const snapshot = await firestore
    .collection('bookmarks')
    .where('authorId', '==', user.uid)
    .orderBy('createdAt', 'desc')
    .get()

  return toDocumentList<ShortVideoBookmark>(snapshot).reverse()
}

export async function getAllShortVideoSubscribes() {
  const firestore = requireShortVideoFirestore()
  const snapshot = await firestore.collection('subscribes').orderBy('createdAt', 'desc').get()

  return toDocumentList<ShortVideoSubscribe>(snapshot).reverse()
}

export function subscribeToShortVideoLikes(
  onChange: (likes: ShortVideoLike[]) => void,
) {
  const firestore = requireShortVideoFirestore()

  return firestore
    .collection('articles_liked')
    .where('type', '==', 'like_video')
    .orderBy('createdAt', 'desc')
    .onSnapshot((snapshot) => {
      onChange(toDocumentList<ShortVideoLike>(snapshot))
    })
}

export async function likeShortVideo({
  profile,
  videoId,
}: {
  readonly profile: DashboardUserProfile
  readonly videoId: string
}) {
  const firestore = requireShortVideoFirestore()
  const user = requireCurrentUser()
  const document = firestore.collection('articles_liked').doc()
  const like: Omit<ShortVideoLike, 'id'> = {
    author: getAuthorSnapshot(profile),
    authorId: user.uid,
    createdAt: Date.now(),
    type: 'like_video',
    videoId,
  }

  await document.set(like)

  return { ...like, id: document.id }
}

export async function toggleShortVideoBookmark({
  currentBookmark,
  profile,
  videoId,
}: {
  readonly currentBookmark: ShortVideoBookmark | null
  readonly profile: DashboardUserProfile
  readonly videoId: string
}) {
  const firestore = requireShortVideoFirestore()
  const user = requireCurrentUser()

  if (currentBookmark) {
    await firestore.doc(`bookmarks/${currentBookmark.id}`).delete()
    return null
  }

  const document = firestore.collection('bookmarks').doc()
  const bookmark: Omit<ShortVideoBookmark, 'id'> = {
    author: getAuthorSnapshot(profile),
    authorId: user.uid,
    createdAt: Date.now(),
    type: 'video',
    videoId,
  }

  await document.set(bookmark)

  return {
    ...bookmark,
    id: document.id,
  }
}

export async function toggleShortVideoSubscribe({
  channelId,
  currentSubscribe,
  profile,
}: {
  readonly channelId: string
  readonly currentSubscribe: ShortVideoSubscribe | null
  readonly profile: DashboardUserProfile
}) {
  const firestore = requireShortVideoFirestore()
  const user = requireCurrentUser()

  if (currentSubscribe) {
    await firestore.doc(`subscribes/${currentSubscribe.id}`).delete()
    return null
  }

  const document = firestore.collection('subscribes').doc()
  const subscribe: Omit<ShortVideoSubscribe, 'id'> = {
    author: getAuthorSnapshot(profile),
    authorId: user.uid,
    channelId,
    createdAt: Date.now(),
  }

  await document.set(subscribe)

  return {
    ...subscribe,
    id: document.id,
  }
}
